
import _ from 'lodash'
import { mapGetters } from 'vuex'
import Modal from '@/components/elements/Modal'
import CurrencyModal from '@/components/molecules/modals/Currency'
import ModalMixin from '@/mixins/modal'
import { gtmDataLayer } from '~/utils/ga'
import { tiktokEvent } from '~/utils/pixels/tiktok'
import { gtmScript } from '~/utils/pixels/gtm'
export default {
  name: 'Selector',
  mixins: [ModalMixin],
  async asyncData({ params, app, store, $axios, error, redirect, route }) {
    try {
      const id = parseInt(params.eid)
      const event = await app.$axios.$get(`/api/event/${id}`)
      const enabled = _.has(event, 'sales_enabled') ? event.sales_enabled : true
      const eventLocaleRoute = app.localePath({
        name: 'events-eid',
        params: { eid: id }
      })

      if (!enabled) {
        return redirect(eventLocaleRoute)
      }

      store.dispatch('event/setEvent', event)

      if (event.is_private) {
        const promocode = await app.$cookiz.get(
          `ts__cookie__private__${event.id}`
        )
        if (promocode) {
          await store.dispatch('event/checkPrivate', {
            event: event.id,
            promocode
          })
        }

        if (!store.getters['event/isUnlocked'](event.id)) {
          return redirect(app.localePath(eventLocaleRoute))
        }
      }

      const zones = await $axios.$get(`api/event/${id}/pricezonegroups`)
      const pricezones = [
        ...zones.data.ungrouped,
        ...zones.data.grouped.map((item) => item.price_zones).flat()
      ]

      const nav = []

      if (event.has_standing) {
        nav.push({
          icon: 'stand',
          title: 'standticket'
        })
      }

      if (event.has_seats) {
        const item = {
          icon: 'seat',
          title: 'seatticket'
        }

        event.seats_first ? nav.unshift(item) : nav.push(item)
      }

      const active = nav.find((item) => item.title === route.query.type)

      return {
        active: active?.title || nav[0].title,
        zones: zones.data,
        pricezones,
        event,
        nav,
        id
      }
    } catch (e) {
      return error({
        statusCode: 404
      })
    }
  },
  head() {
    const i18nSeo = this.$nuxtI18nSeo()
    const gtms = this.event.tracking.filter((tracker) => tracker.type === 'GTM')
    return {
      ...i18nSeo,
      title: this.event.name,
      __dangerouslyDisableSanitizers: ['script', 'noscript'],
      script: [
        ...gtms.map((gtm) => ({
          hid: `script-${gtm.code}`,
          innerHTML: gtmScript(gtm.code),
          type: 'text/javascript',
          charset: 'utf-8'
        }))
      ]
    }
  },
  computed: {
    ...mapGetters({
      cartReady: 'cart/ready',
      mobileLayout: 'mobileLayout',
      count: 'cart/count',
      cartOpen: 'cart/open',
      currency: 'cart/currency'
    }),
    isDisabled() {
      return !this.count
    },
    dateFrom() {
      return this.event.date_from
        ? typeof this.event.date_from === 'string'
          ? this.event.date_from
          : this.event.date_from.date
        : false
    },
    dateTo() {
      return this.event.date_to && this.event.date_to !== '0000-00-00 00:00:00'
        ? typeof this.event.date_to === 'string'
          ? this.event.date_to
          : this.event.date_from.date
        : false
    },
    ready() {
      return this.cartReady
    },
    isMobile() {
      return this.mobileLayout.includes(this.$mq)
    }
  },
  watch: {
    isMobile: {
      handler(val) {
        if (val) {
          this.$store.dispatch('cart/TOGGLE_CART', false)
        }
      }
    },
    active: {
      immediate: true,
      handler(val, oldVal) {
        if (val !== oldVal) {
          this.$router.push({ query: { ...this.$route.query, type: val } })
        }
      }
    },
    ready: {
      immediate: true,
      handler: function(newVal) { // eslint-disable-line
        if (!newVal) return

        const cart = this.$store.getters.content

        for (const cartitemId in cart) {
          /**
           * Client holds last tickets to zone
           * if zone iz empty, but there are tickets in cart from this zone - enable it.
           */
          if (
            !this.event.price_zones[cart[cartitemId].priceZone_id].available
          ) {
            this.event.price_zones[cart[cartitemId].priceZone_id].available = 1
          }
        }
      }
    }
  },
  destroyed() {
    this.$store.dispatch('event/setEvent', {})
  },
  async mounted() {
    // don't open if mobile)
    this.$nextTick(() => {
      if (!this.isMobile) {
        this.$store.dispatch('cart/TOGGLE_CART', true)
      }

      if (this.currency && this.currency !== this.event.currency) {
        return this.$modal.show(
          CurrencyModal,
          {
            eventCurrency: this.event.currency || '',
            cartCurrency: this.currency || ''
          },
          {
            height: 'auto',
            // width: this.modalWidthCalc(),
            adaptive: true,
            scrollable: true,
            clickToClose: false,
            styles: {
              borderRadius: '10px'
            }
          }
        )
      }

      if (this.event.custom_terms) {
        this.$modal.show(
          Modal,
          {
            text: this.event.custom_terms,
            button: this.$t(`buttons.consent`)
          },
          {
            height: 'auto',
            width: this.modalWidthCalc(),
            adaptive: true,
            scrollable: true,
            clickToClose: false
          }
        )
      }
    })

    try {
      const response = await this.$axios.$get(
        `/api/event/${this.event.id}/track_lead`,
        {
          params: {
            page: 'tickets'
          }
        }
      )

      this.event.tracking
        .filter((tracker) => tracker.type === 'facebook')
        .map(({ code }) => {
          return this.$facebook.event(
            code,
            'Lead',
            {},
            response?.tracking_event_id
          )
        })

      this.event.tracking
        .filter((tracker) => tracker.type === 'TikTok')
        .map(({ code }) => {
          return tiktokEvent(
            code,
            'ViewContent',
            {
              contents: [
                {
                  content_id: this.event.id,
                  content_name: this.event.name
                }
              ]
            },
            response?.tracking_event_id
          )
        })
    } catch (e) {
      console.log(e)
    }

    const _gaItems = this.pricezones.map((item) => ({
      item_id: String(item.id),
      item_name: this.event.name,
      currency: item.currency,
      price: item.price
    }))

    gtmDataLayer({
      event: 'view_item',
      ecommerce: {
        items: _gaItems
      }
    })
  },
  methods: {
    navigateToPayment() {
      return this.$router.push(
        this.localePath({
          name: 'payment'
        })
      )
    },
    async fetchPriceZones() {
      try {
        const zones = await this.$axios.$get(
          `api/event/${this.id}/pricezonegroups`
        )
        const pricezones = [
          ...zones.data.ungrouped,
          ...zones.data.grouped.map((item) => item.price_zones).flat()
        ]

        this.zones = zones.data
        this.pricezones = pricezones
      } catch (e) {}
    }
  }
}
